import React, {useMemo} from 'react';
import styles from './FundRepresentativesTable.module.scss';
import {Avatar, Spin, TableColumnConfig, Text} from '@gravity-ui/uikit';
import {FundRepresentativeModel} from '../../entities';
import {formatDateToLocalDate, DataTable, StatusLabel, useAvatarCache, getUncachedImageUrl} from '../../shared';
import {useNavigate} from 'react-router';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

interface FundRepresentativesTableProps {
  representatives: FundRepresentativeModel[];
  isLoading: boolean;
  searchQuery?: string;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const FundRepresentativesTable = ({
  representatives,
  isLoading,
  searchQuery,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: FundRepresentativesTableProps) => {
  const navigate = useNavigate();
  const SKELETON_ROWS = 5;
  const {avatarCacheVersion} = useAvatarCache();

  // Если поиск не задан, пустой список означает, что представителей ещё не заводили,
  // а не то, что фильтр ничего не нашёл.
  const hasSearchQuery = Boolean(searchQuery?.trim());
  const emptyStateTitle = hasSearchQuery ? 'Представитель не найден' : 'Ещё не заведено ни одного представителя';
  const emptyStateDescription = hasSearchQuery
    ? 'Представитель с таким именем, адресом почты или названием фонда не найден. Попробуйте изменить запрос'
    : 'Добавьте первого представителя фонда с помощью кнопки «Добавить»';

  const handleRowClick = (representative: FundRepresentativeModel) => {
    navigate(`/accounts/fund-representatives/profile/${representative.id}`);
  };

  const columns = useMemo<TableColumnConfig<FundRepresentativeModel>[]>(
    () => [
      {
        id: 'displayName',
        name: 'Имя',
        template: (item: FundRepresentativeModel) => (
          <div className={styles.userCellContainer}>
            <Avatar
              imgUrl={getUncachedImageUrl(item.avatarUrl || '', avatarCacheVersion)}
              text={item.displayName ? item.displayName[0] : '?'}
              size="s"
            />
            <Text variant="body-1">{item.displayName}</Text>
          </div>
        ),
      },
      {
        id: 'fundName',
        name: 'Фонд',
        template: (item: FundRepresentativeModel) => item.fundName,
      },
      {
        id: 'lockedAt',
        name: 'Статус',
        template: (item: FundRepresentativeModel) => (
          <StatusLabel lockedAt={item.lockedAt} lastLoggedInAt={item.lastLoggedInAt} />
        ),
      },
      {
        id: 'email',
        name: 'Почта',
        template: (item: FundRepresentativeModel) => item.email,
      },
      {
        id: 'createdAt',
        name: 'Дата регистрации',
        template: (item: FundRepresentativeModel) => formatDateToLocalDate(item.createdAt),
      },
    ],
    [avatarCacheVersion],
  );

  // Следующая страница грузится, когда метка под таблицей появляется в видимой области.
  // rootMargin запускает загрузку чуть заранее, чтобы прокрутка не упиралась в пустоту.
  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootMargin: '200px',
    threshold: 0,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<FundRepresentativeModel>
        isLoading={isLoading}
        data={representatives || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle={emptyStateTitle}
        emptyStateDescription={emptyStateDescription}
        onRowClick={handleRowClick}
      />
      {hasNextPage && <div ref={loadMoreRef} className={styles.loadMoreTrigger} />}
      {isFetchingNextPage && (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      )}
    </div>
  );
};
